import { EntityPropList, ePropType, FormProp, FormPropList, PropData } from '@abp/ng.components/extensible';
import { IdentityUserDto } from '@volo/abp.ng.identity/proxy';
import {
  eIdentityComponents,
  IdentityCreateFormPropContributors,
  IdentityEditFormPropContributors,
  IdentityEntityPropContributors,
} from '@volo/abp.ng.identity';
import { of } from 'rxjs';
import { PermissionConstants } from 'src/core/constants/permission.constant';
import { SipCredentialPanelComponent } from '../components/sip-credential-panel/sip-credential-panel.component';

const EXTENSION_NUMBER_PROPERTY = 'ExtensionNumber';

/**
 * Not a real property on the user record — it only names the slot the SIP credentials panel occupies in
 * the form's prop list. The stored SIP secret is encrypted and deliberately absent from
 * `IdentityUserDto`, so this prop carries no value and binds to nothing; the panel fetches through the
 * authorized `SipCredentialAppService` instead.
 */
const SIP_CREDENTIAL_PANEL_PROPERTY = 'SipCredentialPanel';

function extensionNumberEntityPropContributor(propList: EntityPropList<IdentityUserDto>): void {
  const propNode = propList.find(node => node.value.name === EXTENSION_NUMBER_PROPERTY);
  if (!propNode) return;

  Object.assign(propNode.value, {
    valueResolver: (data: PropData<IdentityUserDto>) =>
      of(data.record.extraProperties?.[EXTENSION_NUMBER_PROPERTY] || '-'),
  });
}

function extensionNumberFormPropContributor(propList: FormPropList<IdentityUserDto>): void {
  const propNode = propList.find(node => node.value.name === EXTENSION_NUMBER_PROPERTY);
  if (!propNode) return;

  Object.assign(propNode.value, { type: ePropType.Number });
}

/**
 * Appends the SIP credentials panel to the user edit form.
 *
 * Edit-only: it reads credentials for an existing user, so it has nothing to show while a user is still
 * being created. `permission` is what ABP's `*abpPermission` on the rendered prop checks, so the panel
 * is hidden outright from anyone without agent-management rights — the same permission
 * `SipCredentialAppService` enforces server-side for reading another agent's secret.
 */
function sipCredentialFormPropContributor(propList: FormPropList<IdentityUserDto>): void {
  propList.addTail(
    FormProp.create<IdentityUserDto>({
      type: ePropType.String,
      name: SIP_CREDENTIAL_PANEL_PROPERTY,
      displayName: 'AgentManagement::Sip:SectionTitle',
      permission: PermissionConstants.agentManagement,
      template: SipCredentialPanelComponent,
    })
  );
}

export const IDENTITY_ENTITY_PROP_CONTRIBUTORS: IdentityEntityPropContributors = {
  [eIdentityComponents.Users]: [extensionNumberEntityPropContributor],
};

export const IDENTITY_FORM_PROP_CONTRIBUTORS: IdentityCreateFormPropContributors = {
  [eIdentityComponents.Users]: [extensionNumberFormPropContributor],
};

export const IDENTITY_EDIT_FORM_PROP_CONTRIBUTORS: IdentityEditFormPropContributors = {
  [eIdentityComponents.Users]: [
    extensionNumberFormPropContributor,
    sipCredentialFormPropContributor,
  ],
};
